import type { Issue } from './types';
import { mentions } from './mentions';

/**
 * WHAT HAS HAPPENED ON A CARD SINCE YOU LAST OPENED IT.
 *
 * A board of forty cards tells you everything except the one thing you came for:
 * whether anything has moved since you last looked. The Activity screen answers
 * it, but only by leaving the board — and the answer you want is per card, on
 * the card.
 *
 * THREE RULES, and each is there to stop the mark becoming wallpaper.
 *
 *   SINCE YOU LAST OPENED THAT CARD. Not since you last looked at the board, and
 *   not "recently" — those are somebody else's news as much as yours, and
 *   neither ever clears.
 *
 *   ONLY CARDS THAT CONCERN YOU. Assigned to you, mentioning you, or one you
 *   have joined by commenting. On a busy board "any change" would mark most
 *   cards most of the time, and a mark that is always on says nothing.
 *
 *   NEVER YOUR OWN DOING. You know what you just did. A card that lights up
 *   because you moved it is noise that also teaches people to ignore the mark.
 */
export type WhatIsNew = 'comment' | 'change' | 'both' | null;

/** Whether this card is any of your business — see the second rule. */
export function concernsMe(issue: Issue, me: string): boolean {
  const mine = me.toLowerCase();
  if (!mine) return false;
  if ((issue.assignee ?? '').toLowerCase() === mine) return true;
  for (const c of issue.comments ?? []) {
    // You joined the conversation, or somebody addressed you in it.
    if ((c.author ?? '').toLowerCase() === mine) return true;
    if (mentions(c.text ?? '', mine)) return true;
  }
  return false;
}

/**
 * What is new on this card for this person, or null.
 *
 * `seenAt` is when they last opened it — 0 or absent means never, and a card
 * they have never opened is judged on everything it holds. That is deliberate:
 * being assigned something you have not read is exactly the case worth marking.
 */
export function whatIsNew(issue: Issue, me: string, seenAt = 0): WhatIsNew {
  const mine = me.toLowerCase();
  if (!mine || !concernsMe(issue, mine)) return null;

  /*
   * A removed comment still counts as a comment that happened — it was said,
   * and taking it back is itself a thing worth knowing about. Filtering here
   * would also disagree with the card's own comment count, which does not.
   */
  const talked = (issue.comments ?? []).some(
    (c) => (c.createdAt ?? 0) > seenAt && (c.author ?? '').toLowerCase() !== mine
  );
  const changed = (issue.activity ?? []).some(
    (a) => (a.at ?? 0) > seenAt && (a.who ?? '').toLowerCase() !== mine
  );

  if (talked && changed) return 'both';
  if (talked) return 'comment';
  if (changed) return 'change';
  return null;
}
